// Hero completeness ring + AI Insights snapshot gauge targets
export const heroRingPercent = 80;
export const insightsGaugePercent = 86;
export const profileStrengthPercent = 80;

// Quick-stats shelf (4 cards). `format` matches the counter formatter to use.
export const quickStats = [
  {
    id: "years",
    icon: "briefcase",
    accent: "blue",
    target: 4,
    format: "int",
    label: "Years Experience",
  },
  {
    id: "views",
    icon: "eye",
    accent: "green",
    target: 1204,
    format: "comma",
    label: "Profile Views",
  },
  {
    id: "rating",
    icon: "star",
    accent: "amber",
    target: 4.8,
    format: "decimal",
    label: "Rating",
  },
  {
    id: "match",
    icon: "sparkles",
    accent: "indigo",
    target: 86,
    format: "percent",
    label: "AI Match Score",
  },
];

export const strengthChecklist = [
  { label: "Resume Uploaded", done: true },
  { label: "Skills Added", done: true },
  { label: "Psychometric Test Taken", done: true },
  { label: "Work Experience Added", done: true },
  { label: "Certificates Uploaded", done: false },
];

export const checklistConfig = [
  ["Resume Uploaded", "completedResumeUpload"],
  ["Skills Added", "completedSkillsInformation"],
  ["Work Experience Added", "completedWorkHistory"],
  ["Education Added", "completedEducation"],
  ["Salary Preferences Added", "completedWorkCompensation"],
  ["Psychometric Test Taken", "submittedPsychometricTest"],
];

export const aiInsightsSnapshot = {
  matchScore: insightsGaugePercent,
  strengths: ["Teamwork", "Creativity", "Collaboration"],
  growthAreas: ["Adaptability", "Stress Tolerance"],
};

// Career Plan: past test attempts, each with its own improvement tips + recommended courses.
export const careerAttempts = [
  {
    date: "Jun 18, 2026",
    score: 86,
    improvements: [
      {
        icon: "wind",
        title: "Build stress-resilience habits",
        tip: "Practice a simple breathing or prioritization routine for when workload spikes — small resets prevent pressure from compounding.",
      },
      {
        icon: "refreshAlert",
        title: "Practice adaptive decision-making",
        tip: "Deliberately seek out tasks with shifting requirements to build comfort with change instead of avoiding it.",
      },
      {
        icon: "scale",
        title: "Slow down high-pressure calls",
        tip: "Use a quick pros/cons checklist before deciding on time-critical items so judgement holds up under a deadline.",
      },
    ],
    courses: [
      {
        title: "Managing Pressure on High-Stakes Projects",
        desc: "Practical techniques for staying composed and decisive when deadlines tighten.",
      },
      {
        title: "Adaptive Planning for Project Managers",
        desc: "How to re-plan quickly and confidently when project scope shifts mid-stream.",
      },
    ],
  },
  {
    date: "Feb 4, 2026",
    score: 79,
    improvements: [
      {
        icon: "usersGroup",
        title: "Pair with mentors on ambiguity",
        tip: "Shadow a senior teammate handling an uncertain project to pick up their pattern-recognition shortcuts.",
      },
      {
        icon: "message2",
        title: "Tighten stakeholder communication",
        tip: "Summarize decisions and next steps in writing after every stakeholder call to reduce follow-up back-and-forth.",
      },
    ],
    courses: [
      {
        title: "Stakeholder Communication Essentials",
        desc: "A short course on setting expectations clearly with clients and cross-functional teams.",
      },
      {
        title: "Introduction to Agile Project Delivery",
        desc: "Core Agile/Scrum concepts for planning and tracking iterative project work.",
      },
    ],
  },
  {
    date: "Sep 21, 2025",
    score: 71,
    improvements: [
      {
        icon: "targetArrow",
        title: "Sharpen prioritization skills",
        tip: "Use a simple impact-vs-effort matrix before committing to a sprint so lower-value tasks don\u2019t crowd out what matters.",
      },
    ],
    courses: [
      {
        title: "Time & Priority Management for Managers",
        desc: "Frameworks for deciding what to work on first when everything feels urgent.",
      },
    ],
  },
];

export const careerPlanUnlockPrice = "₦5,000";

// Share networks — build(url, text) returns the share URL for that network.
export const shareNetworks = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    color: "#25D366",
    build: (url, text) =>
      `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    color: "#0A66C2",
    build: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
  {
    id: "x",
    label: "X",
    color: "#0d1b3e",
    build: (url, text) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    id: "facebook",
    label: "Facebook",
    color: "#1877F2",
    build: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: "email",
    label: "Email",
    color: "#64748b",
    build: (url, text) =>
      `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`,
  },
];

export function buildShareText(name) {
  return `Check out ${name}\u2019s profile on Awasource`;
}

export const testResults = [
  {
    id: "architecture",
    skill: "Software Architecture Assessment",
    role: "Software Architect",
    summary:
      "The candidate demonstrates a basic understanding of architectural concepts but lacks depth in their responses.",
    weaknesses:
      "Responses lack detail and specific examples, no clear articulation of challenges faced in past projects, limited demonstration of critical thinking in architectural decisions",
    strengths:
      "Familiarity with key architectural concepts, ability to identify security considerations, understanding of the importance of documentation",
    improvements: [
      {
        title: "Depth of knowledge in architectural patterns",
        detail:
          "Responses were too brief and did not showcase a comprehensive understanding of architectural patterns.",
      },
      {
        title: "Real-world application of architectural decisions",
        detail:
          "The candidate did not provide specific examples or outcomes from past experiences.",
      },
      {
        title: "Critical analysis of technology stack selection",
        detail:
          "The answers did not reflect a nuanced approach to selecting technology stacks based on project needs.",
      },
    ],
    resources: [
      {
        title: "System Architecture Patterns",
        description: "An overview of various system architecture patterns and their applications.",
      },
      {
        title: "Microservices vs Monolithic Architecture",
        description: "A detailed explanation of the differences between microservices and monolithic architectures.",
      },
      {
        title: "Choosing the Right Tech Stack",
        description: "Guidelines on how to choose the right technology stack for your projects.",
      },
    ],
  },
];
